// composeBadgeWide.js — the LANDSCAPE Bravos member badge ("proud member of this community").
//
// Same engine as composeBadge.js (whose drawing helpers it reuses), different grid and a
// different message. 1200x627 because these are posted to LinkedIn, where that ratio fills
// the feed card edge to edge with no letterboxing.
//
// Geometry is a FIXED convention shared with the Machina Content > Visuals designer template
// (static/badge/badge-frame-wide-template.svg): the photo circle middle-right at (930,280)
// r155, the member's name on the baseline just under it at y 500. THE APP DRAWS NOTHING ELSE —
// the headline, strapline, logo and call to action all live in the frame artwork, so the
// design can change without a deploy.
//
// All source images arrive as base64 data: URIs (from /api/bravos/my-badge), so they are
// same-origin to the canvas — canvas.toBlob() never hits a tainted-canvas error.

import {
  FONT, loadImage, drawPhotoInCircle, drawContain, fitText, luminance, shade, roundRect,
} from './composeBadge';

const W = 1200;
const H = 627;
const PHOTO_CX = 930;
const PHOTO_CY = 280;
const PHOTO_R = 155;
const NAME_Y = 500;          // alphabetic baseline
// The name sits on a filled pill in the brand colour, so it reads the same on any artwork and
// the designer knows exactly what lands there. The pill hugs the name but never grows past the
// band the template reserves (x 720-1140), so a long name can't run into the design.
const NAME_BAND_MAXW = 420;
const NAME_PAD_X = 24;
const NAME_MAXW = NAME_BAND_MAXW - NAME_PAD_X * 2;
const NAME_BAND_H = 52;
const NAME_BAND_TOP = NAME_Y - 36;   // 464..516, centred in the reserved 460..520
// Everything left of the photo column belongs to the designer.
const DESIGN_MAXW = 640;

// Wrap text into at most maxLines lines that each fit maxWidth, ellipsizing the last one.
const wrapLines = (ctx, text, maxWidth, maxLines) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth || !line) {
      line = next;
    } else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  let last = kept[maxLines - 1];
  while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
  kept[maxLines - 1] = `${last}…`;
  return kept;
};

// Branded frame drawn entirely in code, for a community that hasn't uploaded artwork yet.
// This is the ONLY place the app draws the headline — an uploaded frame carries its own.
const drawFallbackFrame = (ctx, { logo, primaryColor, secondaryColor, headline }) => {
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, shade(primaryColor, 26));
  grad.addColorStop(1, shade(primaryColor, -46));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  if (logo) drawContain(ctx, logo, 70, 64, 300, 110);

  if (headline) {
    ctx.save();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    // A pale brand colour would swallow white text, so follow the gradient we just painted.
    ctx.fillStyle = luminance(shade(primaryColor, -10)) > 0.6 ? '#0d1b2a' : '#ffffff';
    ctx.font = `700 52px ${FONT}`;
    const lines = wrapLines(ctx, headline, DESIGN_MAXW, 3);
    lines.forEach((line, i) => ctx.fillText(line, 70, 300 + i * 64));
    ctx.restore();
  }

  // ring around the photo circle
  ctx.save();
  ctx.lineWidth = 10;
  ctx.strokeStyle = secondaryColor || '#ffffff';
  ctx.beginPath();
  ctx.arc(PHOTO_CX, PHOTO_CY, PHOTO_R + 6, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
};

export const composeBadgeWide = async ({
  photoDataUri, frameDataUri, logoDataUri,
  fullName, headline,
  primaryColor = '#002335', secondaryColor = '#ffffff',
}) => {
  const [photo, frame, logo] = await Promise.all([
    loadImage(photoDataUri), loadImage(frameDataUri), loadImage(logoDataUri),
  ]);
  if (!photo) throw new Error('noPhoto');

  try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (e) { /* fonts optional */ }

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  if (frame) {
    // Backdrop first, so anything the artwork leaves transparent still reads as brand colour.
    ctx.fillStyle = shade(primaryColor, -20);
    ctx.fillRect(0, 0, W, H);
    drawPhotoInCircle(ctx, photo, PHOTO_CX, PHOTO_CY, PHOTO_R);
    // The frame goes OVER the photo: its transparent circle reveals the photo, and any ring or
    // shadow the designer drew around that circle lands on top of it.
    ctx.drawImage(frame, 0, 0, W, H);
  } else {
    drawFallbackFrame(ctx, { logo, primaryColor, secondaryColor, headline });
    drawPhotoInCircle(ctx, photo, PHOTO_CX, PHOTO_CY, PHOTO_R);
    // redraw the ring on top so the photo doesn't overlap it
    ctx.save();
    ctx.lineWidth = 10;
    ctx.strokeStyle = secondaryColor || '#ffffff';
    ctx.beginPath();
    ctx.arc(PHOTO_CX, PHOTO_CY, PHOTO_R + 6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // The name on a filled pill in the brand colour. Deliberately not "pick light or dark to suit
  // the artwork": a solid band reads identically on every frame, and the designer can see in the
  // template exactly what the app will put there.
  if (fullName) {
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    // Weight 700: index.html only loads Poppins 500 and 700, so 600 would be synthesised.
    // fitText leaves ctx.font set at the size it settled on, so measure after it.
    const t = fitText(ctx, fullName, 34, 700, NAME_MAXW);
    const bandW = Math.min(NAME_BAND_MAXW, ctx.measureText(t).width + NAME_PAD_X * 2);
    roundRect(ctx, PHOTO_CX - bandW / 2, NAME_BAND_TOP, bandW, NAME_BAND_H, NAME_BAND_H / 2);
    ctx.fillStyle = primaryColor;
    ctx.fill();
    // A pale brand colour would swallow white text.
    ctx.fillStyle = luminance(primaryColor) > 0.6 ? '#0d1b2a' : '#ffffff';
    ctx.fillText(t, PHOTO_CX, NAME_Y);
    ctx.restore();
  }

  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlobFailed'))), 'image/png');
  });
};
